import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { createForm } from '@sonofabit/ng-core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { Router } from '@angular/router';
import { BuildingApplicationItemDto, BuildingApplicationService } from '@artemis-software/wr-api';
import { firstValueFrom } from 'rxjs';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import {
  BuildingApplicationApproveDialogComponent
} from '@components/dialog/building-application-approve-dialog/building-application-approve-dialog.component';
import {
  BuildingApplicationRejectDialogComponent
} from '@components/dialog/building-application-reject-dialog/building-application-reject-dialog.component';
import { routeParam } from '@/utils/signals/router';
import { loader } from '@/utils/signals/loader';

@Component({
  standalone: false,
  selector: 'wr-building-application-admin-detail',
  templateUrl: './building-application-admin-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuildingApplicationAdminDetailComponent {
  private readonly router = inject(Router);
  private readonly buildingApplicationService = inject(BuildingApplicationService);
  private readonly toastrService = inject(NbToastrService);
  private readonly dialogService = inject(NbDialogService);
  protected readonly applicationStatus = BuildingApplicationItemDto.StatusEnum;

  readonly baseDateTab = 'Grunddaten';
  readonly buildingSectionTab = 'Eingänge';

  readonly buildingApplicationId = routeParam('id', true);
  readonly loader = loader();

  readonly buildingApplication = asyncComputed(()=>{
    return this.buildingApplicationService.findById(this.buildingApplicationId());
  }, undefined, this.loader);

  readonly form = computed(() => {
    const buildingApplication = this.buildingApplication();

    return createForm(({ field }) => ({
      id: field({ value: buildingApplication?.id }),
      baseData: field({ value: buildingApplication?.baseData ?? null }),
      allocationData: field({ value: buildingApplication?.allocationData ?? null }),
      accessData: field({ value: buildingApplication?.accessData ?? null }),
      amenityData: field({ value: buildingApplication?.amenityData ?? null }),
      buildingSections: field({ value: buildingApplication?.groupedBuildingSections ?? [] }),
    }));
  });

  constructor() {
    effect(() => {
      this.form().disable();
    });
  }

  async navigateBack(): Promise<void> {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      await this.router.navigate(['buildingApplicationsAdmin']);
    }
  }

  async approveBuildingApplication() {
    const application = this.buildingApplication();
    const loader = this.loader.add();
    if (!application?.id) return;

    const result = await firstValueFrom(
      this.dialogService.open(BuildingApplicationApproveDialogComponent, {
        context: {
          id: application.id
        }
      }).onClose
    );

    if (result?.internalNumber) {
      try {
        loader.set(true);

        await firstValueFrom(this.buildingApplicationService.approveBuildingRequest(result));

        this.toastrService.success('Antrag wurde erfolgreich genehmigt', 'Erfolg');
        await this.navigateBack();
      } catch (e) {
        this.toastrService.danger('Fehler beim Genehmigen des Antrags', 'Fehler');
      } finally {
        loader.stop();
      }
    }
  }

  async rejectBuildingApplication() {
    const application = this.buildingApplication();
    const loader = this.loader.add();
    if (!application?.id) return;

    const result = await firstValueFrom(
      this.dialogService.open(BuildingApplicationRejectDialogComponent, {
        context: {
          id: application.id
        }
      }).onClose
    );

    if (result?.rejectComment) {
      try {
        loader.set(true);

        await firstValueFrom(this.buildingApplicationService.rejectBuildingRequest(result));

        this.toastrService.success('Der Antrag wurde erfolgreich abgelehnt.', 'Abgelehnt');
        await this.navigateBack();
      } catch (e) {
        this.toastrService.danger('Fehler beim Ablehnen des Antrags.', 'Fehler');
      } finally {
        loader.stop();
      }
    }
  }
}
